import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  styled,
  Switch,
  TextField as MuiTextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Flag as FlagIcon,
  Sort as SortIcon,
} from '@mui/icons-material'
import { Icon } from '@iconify/react'
import moment from 'moment'
import { MilestoneDetail } from '../../../../../domain/entity/MilestoneEntity'
import {
  formatDateForApi,
  DISPLAY_DATE_FORMAT,
} from '../../../../../utils/date'
import BigFireIcon from '../../../../components/icons/BigFireIcon'
import { DateInput } from '../../../../components/inputs/DatePicker'
import { Button } from '../../../../components/buttons'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import {
  useMilestoneDialog,
  MilestoneWithState,
  SortOption,
  FormState,
  DEFAULT_ICON,
  DEFAULT_COLOR,
  PRESET_ICONS,
  PRESET_COLORS,
  getMilestoneStatus,
  getEditStateBackgroundColor,
} from '../../hooks'

type Props = {
  open: boolean
  onClose: () => void
  initialMilestones?: MilestoneDetail[]
  onSaveComplete?: () => void
  isLoadingMilestones?: boolean
}

const MilestoneDialog = ({
  open,
  onClose,
  initialMilestones,
  onSaveComplete,
  isLoadingMilestones,
}: Props) => {
  const {
    displayMilestones,
    selectedMilestone,
    hasChanges,
    isSaving,
    confirmDialogOpen,
    rightPanelMode,
    sortOption,
    setSortOption,
    formState,
    handleDialogEntered,
    handleCloseDialog,
    handleConfirmClose,
    handleCancelClose,
    handleSelectMilestone,
    handleStartEdit,
    handleStartAdd,
    handleAddMilestone,
    handleFormFieldChange,
    handleUpdateField,
    handleMarkDelete,
    handleUndoDelete,
    handleToggleAchieved,
    handleSave,
  } = useMilestoneDialog({ initialMilestones, onClose, onSaveComplete })

  const isLoading = isLoadingMilestones ?? false

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth={true}
      TransitionProps={{ onEntered: handleDialogEntered }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${colorPalette.monotone[2]}`,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagIcon sx={{ color: colorPalette.skyBlue[7] }} />
          <Typography variant="h6" component="span">
            {intl.formatMessage({ id: 'milestone.dialog.title' })}
          </Typography>
          {hasChanges && (
            <Chip
              label={intl.formatMessage({ id: 'milestone.unsavedChanges' })}
              size="small"
              color="warning"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <IconButton onClick={handleCloseDialog} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: 520 }}>
          <LeftPanel
            milestones={displayMilestones}
            selectedMilestone={selectedMilestone}
            sortOption={sortOption}
            isLoading={isLoading}
            onSortChange={setSortOption}
            onAdd={handleStartAdd}
            onSelect={handleSelectMilestone}
            onEdit={handleStartEdit}
            onDelete={handleMarkDelete}
            onUndoDelete={handleUndoDelete}
            onToggleAchieved={handleToggleAchieved}
          />

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {rightPanelMode === 'view' && selectedMilestone && (
              <ViewPanel
                milestone={selectedMilestone}
                onEdit={handleStartEdit}
              />
            )}

            {rightPanelMode === 'edit' && selectedMilestone && (
              <FormPanel
                title={intl.formatMessage({ id: 'milestone.panel.edit' })}
                formState={formState}
                onFieldChange={handleUpdateField}
              />
            )}

            {rightPanelMode === 'add' && (
              <>
                <FormPanel
                  title={intl.formatMessage({ id: 'milestone.panel.add' })}
                  formState={formState}
                  showAchievedToggle={false}
                  onFieldChange={handleFormFieldChange}
                />
                <Box
                  sx={{
                    p: 2,
                    borderTop: `1px solid ${colorPalette.monotone[2]}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    colorPattern="skyBlue"
                    disabled={!formState.name || !formState.milestoneDate}
                    onClick={handleAddMilestone}
                  >
                    {intl.formatMessage({ id: 'milestone.button.addItem' })}
                  </Button>
                </Box>
              </>
            )}

            {rightPanelMode === 'view' && !selectedMilestone && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colorPalette.monotone[4],
                }}
              >
                <Typography>
                  {intl.formatMessage({ id: 'milestone.selectPrompt' })}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${colorPalette.monotone[2]}`,
          px: 3,
          py: 2,
        }}
      >
        <Button
          colorPattern="monotone"
          variant="outlined"
          onClick={handleCloseDialog}
        >
          {intl.formatMessage({ id: 'milestone.button.cancel' })}
        </Button>
        <Button
          colorPattern="skyBlue"
          disabled={!hasChanges || isSaving}
          onClick={handleSave}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {isSaving && <CircularProgress size={16} color="inherit" />}
          {isSaving
            ? intl.formatMessage({ id: 'milestone.button.saving' })
            : intl.formatMessage({ id: 'milestone.button.save' })}
        </Button>
      </DialogActions>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
      >
        <DialogTitle>
          {intl.formatMessage({ id: 'milestone.confirm.title' })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage({ id: 'milestone.confirm.message' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            colorPattern="monotone"
            variant="outlined"
            onClick={handleCancelClose}
          >
            {intl.formatMessage({ id: 'milestone.confirm.cancel' })}
          </Button>
          <Button colorPattern="pink" onClick={handleConfirmClose}>
            {intl.formatMessage({ id: 'milestone.confirm.discard' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

const LeftPanel = ({
  milestones,
  selectedMilestone,
  sortOption,
  isLoading,
  onSortChange,
  onAdd,
  onSelect,
  onEdit,
  onDelete,
  onUndoDelete,
  onToggleAchieved,
}: {
  milestones: MilestoneWithState[]
  selectedMilestone: MilestoneWithState | null
  sortOption: SortOption
  isLoading: boolean
  onSortChange: (option: SortOption) => void
  onAdd: () => void
  onSelect: (uuid: string) => void
  onEdit: (uuid: string) => void
  onDelete: (uuid: string) => void
  onUndoDelete: (uuid: string) => void
  onToggleAchieved: (uuid: string) => void
}) => (
  <Box
    sx={{
      width: 380,
      borderRight: `1px solid ${colorPalette.monotone[2]}`,
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box
      sx={{
        p: 2,
        borderBottom: `1px solid ${colorPalette.monotone[2]}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {intl.formatMessage(
            { id: 'milestone.list.title' },
            { count: milestones.length }
          )}
        </Typography>
        <Button
          size="s"
          colorPattern="skyBlue"
          variant="filled"
          onClick={onAdd}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          {intl.formatMessage({ id: 'milestone.button.add' })}
        </Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SortIcon sx={{ fontSize: 16, color: colorPalette.monotone[4] }} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={sortOption}
            onChange={e => onSortChange(e.target.value as SortOption)}
            sx={{ fontSize: 12, '& .MuiSelect-select': { py: 0.75 } }}
          >
            <MenuItem value="date_asc">
              {intl.formatMessage({ id: 'milestone.sort.dateAsc' })}
            </MenuItem>
            <MenuItem value="date_desc">
              {intl.formatMessage({ id: 'milestone.sort.dateDesc' })}
            </MenuItem>
            <MenuItem value="achieved_first">
              {intl.formatMessage({ id: 'milestone.sort.achievedFirst' })}
            </MenuItem>
            <MenuItem value="unachieved_first">
              {intl.formatMessage({ id: 'milestone.sort.unachievedFirst' })}
            </MenuItem>
            <MenuItem value="created_asc">
              {intl.formatMessage({ id: 'milestone.sort.createdAsc' })}
            </MenuItem>
            <MenuItem value="created_desc">
              {intl.formatMessage({ id: 'milestone.sort.createdDesc' })}
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>

    <List dense={true} sx={{ flex: 1, overflow: 'auto', py: 0 }}>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 6,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : milestones.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 4,
          }}
        >
          <Typography color="text.secondary">
            {intl.formatMessage({ id: 'milestone.empty' })}
          </Typography>
        </Box>
      ) : null}
      {milestones.map(milestone => (
        <MilestoneListItem
          key={milestone.uuid}
          milestone={milestone}
          isSelected={selectedMilestone?.uuid === milestone.uuid}
          onSelect={onSelect}
          onEdit={onEdit}
          onDelete={onDelete}
          onUndoDelete={onUndoDelete}
          onToggleAchieved={onToggleAchieved}
        />
      ))}
    </List>
  </Box>
)

const MilestoneListItem = ({
  milestone,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onUndoDelete,
  onToggleAchieved,
}: {
  milestone: MilestoneWithState
  isSelected: boolean
  onSelect: (uuid: string) => void
  onEdit: (uuid: string) => void
  onDelete: (uuid: string) => void
  onUndoDelete: (uuid: string) => void
  onToggleAchieved: (uuid: string) => void
}) => {
  const status = getMilestoneStatus(milestone)
  const isDeleted = milestone.editState === 'deleted'

  return (
    <ListItem
      sx={{
        borderBottom: `1px solid ${colorPalette.monotone[1]}`,
        cursor: isDeleted ? 'default' : 'pointer',
        bgcolor: isSelected
          ? colorPalette.skyBlue[0]
          : getEditStateBackgroundColor(milestone.editState),
        opacity: isDeleted ? 0.5 : 1,
        textDecoration: isDeleted ? 'line-through' : 'none',
        '&:hover': {
          bgcolor: isDeleted
            ? getEditStateBackgroundColor(milestone.editState)
            : isSelected
              ? colorPalette.skyBlue[0]
              : colorPalette.monotone[1],
        },
      }}
      onClick={() => !isDeleted && onSelect(milestone.uuid)}
      secondaryAction={
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isDeleted ? (
            <Tooltip
              title={intl.formatMessage({ id: 'milestone.action.undoDelete' })}
            >
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  onUndoDelete(milestone.uuid)
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <Tooltip
                title={intl.formatMessage({ id: 'milestone.action.edit' })}
              >
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation()
                    onEdit(milestone.uuid)
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={intl.formatMessage({ id: 'milestone.action.delete' })}
              >
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation()
                    onDelete(milestone.uuid)
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      }
    >
      <Tooltip
        title={
          status === 'achieved'
            ? intl.formatMessage({ id: 'milestone.status.achieved' })
            : status === 'overdue'
              ? intl.formatMessage({ id: 'milestone.status.overdue' })
              : intl.formatMessage({ id: 'milestone.status.pending' })
        }
      >
        <IconButton
          size="small"
          disabled={isDeleted}
          onClick={e => {
            e.stopPropagation()
            onToggleAchieved(milestone.uuid)
          }}
          sx={{ mr: 0.5, p: 0.5 }}
        >
          {status === 'achieved' ? (
            <CheckCircleIcon
              sx={{ fontSize: 20, color: colorPalette.green[5] }}
            />
          ) : status === 'overdue' ? (
            <BigFireIcon style={{ fontSize: 20 }} />
          ) : (
            <CheckCircleOutlineIcon
              sx={{ fontSize: 20, color: colorPalette.monotone[4] }}
            />
          )}
        </IconButton>
      </Tooltip>
      <ListItemIcon
        sx={{ minWidth: 28, color: milestone.color ?? DEFAULT_COLOR }}
      >
        <Icon
          icon={milestone.iconName ?? DEFAULT_ICON}
          width={18}
          height={18}
          color={milestone.color ?? DEFAULT_COLOR}
        />
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{milestone.name}</span>
            {milestone.editState === 'added' && (
              <Chip
                label={intl.formatMessage({ id: 'milestone.badge.new' })}
                size="small"
                sx={{
                  height: 16,
                  fontSize: 10,
                  bgcolor: colorPalette.green[1],
                  color: colorPalette.green[7],
                }}
              />
            )}
            {milestone.editState === 'edited' && (
              <Chip
                label={intl.formatMessage({ id: 'milestone.badge.edited' })}
                size="small"
                sx={{
                  height: 16,
                  fontSize: 10,
                  bgcolor: colorPalette.yellow[1],
                  color: colorPalette.yellow[7],
                }}
              />
            )}
          </Box>
        }
        secondary={moment(milestone.milestoneDate).format(DISPLAY_DATE_FORMAT)}
        primaryTypographyProps={{ fontSize: 13 }}
        secondaryTypographyProps={{ fontSize: 11 }}
      />
    </ListItem>
  )
}

const ViewPanel = ({
  milestone,
  onEdit,
}: {
  milestone: MilestoneWithState
  onEdit: (uuid: string) => void
}) => {
  const status = getMilestoneStatus(milestone)

  return (
    <>
      <Box sx={{ p: 2, borderBottom: `1px solid ${colorPalette.monotone[2]}` }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {intl.formatMessage({ id: 'milestone.panel.basicInfo' })}
        </Typography>
      </Box>

      <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              borderRadius: 1,
              bgcolor:
                status === 'achieved'
                  ? colorPalette.green[0]
                  : status === 'overdue'
                    ? colorPalette.pink[0]
                    : colorPalette.monotone[1],
            }}
          >
            {status === 'achieved' ? (
              <>
                <CheckCircleIcon sx={{ color: colorPalette.green[5] }} />
                <Typography
                  variant="body2"
                  sx={{ color: colorPalette.green[7] }}
                >
                  {intl.formatMessage({ id: 'milestone.status.achieved' })}
                  {milestone.achievedAt && ` (${milestone.achievedAt})`}
                </Typography>
              </>
            ) : status === 'overdue' ? (
              <>
                <BigFireIcon style={{ fontSize: 24 }} />
                <Typography
                  variant="body2"
                  sx={{ color: colorPalette.pink[7] }}
                >
                  {intl.formatMessage({
                    id: 'milestone.status.overdueMessage',
                  })}
                </Typography>
              </>
            ) : (
              <>
                <CheckCircleOutlineIcon
                  sx={{ color: colorPalette.monotone[4] }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: colorPalette.monotone[6] }}
                >
                  {intl.formatMessage({ id: 'milestone.status.pending' })}
                </Typography>
              </>
            )}
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {intl.formatMessage({ id: 'milestone.field.name' })}
            </Typography>
            <Typography variant="body2">{milestone.name}</Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {intl.formatMessage({ id: 'milestone.field.date' })}
            </Typography>
            <Typography variant="body2">
              {moment(milestone.milestoneDate).format(DISPLAY_DATE_FORMAT)}
            </Typography>
          </Box>

          {milestone.description && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {intl.formatMessage({ id: 'milestone.field.description' })}
              </Typography>
              <Typography variant="body2">{milestone.description}</Typography>
            </Box>
          )}

          <Box
            sx={{
              bgcolor: colorPalette.monotone[1],
              p: 1.5,
              borderRadius: 1,
              mt: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage({ id: 'milestone.field.created' })}:
              </Typography>
              {milestone.createdBy?.iconUrl && (
                <Avatar
                  src={milestone.createdBy.iconUrl}
                  sx={{ width: 16, height: 16 }}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {milestone.createdBy?.name} ({milestone.createdAt})
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            colorPattern="skyBlue"
            variant="outlined"
            onClick={() => onEdit(milestone.uuid)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
            {intl.formatMessage({ id: 'milestone.button.edit' })}
          </Button>
        </Box>
      </Box>
    </>
  )
}

const FormPanel = ({
  title,
  formState,
  showAchievedToggle = true,
  onFieldChange,
}: {
  title: string
  formState: FormState
  showAchievedToggle?: boolean
  onFieldChange: (field: keyof FormState, value: string | boolean) => void
}) => (
  <>
    <Box sx={{ p: 2, borderBottom: `1px solid ${colorPalette.monotone[2]}` }}>
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
    </Box>

    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            {intl.formatMessage({ id: 'milestone.field.name' })} *
          </Typography>
          <TextField
            size="small"
            fullWidth={true}
            value={formState.name}
            onChange={e => onFieldChange('name', e.target.value)}
            placeholder={intl.formatMessage({
              id: 'milestone.placeholder.name',
            })}
          />
        </Box>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            {intl.formatMessage({ id: 'milestone.field.date' })} *
          </Typography>
          <Box
            sx={{
              border: `1px solid ${colorPalette.monotone[2]}`,
              borderRadius: '4px',
              px: 1.5,
              py: 0.75,
              fontSize: 14,
              '& input': { fontSize: 14, color: colorPalette.monotone[9] },
              '&:hover': { borderColor: colorPalette.monotone[10] },
              '&:focus-within': {
                borderColor: colorPalette.skyBlue[7],
                borderWidth: '2px',
                px: 1.375,
                py: 0.625,
              },
            }}
          >
            <DateInput
              value={
                formState.milestoneDate
                  ? new Date(formState.milestoneDate)
                  : undefined
              }
              onChange={date => {
                onFieldChange(
                  'milestoneDate',
                  date ? (formatDateForApi(date) ?? '') : ''
                )
              }}
              placeholder="YYYY/MM/DD"
            />
          </Box>
        </Box>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            {intl.formatMessage({ id: 'milestone.field.description' })}
          </Typography>
          <TextField
            size="small"
            fullWidth={true}
            multiline={true}
            rows={2}
            value={formState.description}
            onChange={e => onFieldChange('description', e.target.value)}
            placeholder={intl.formatMessage({
              id: 'milestone.placeholder.description',
            })}
          />
        </Box>

        {showAchievedToggle && (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formState.achieved}
                  onChange={e => onFieldChange('achieved', e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                formState.achieved
                  ? intl.formatMessage({ id: 'milestone.status.achieved' })
                  : intl.formatMessage({ id: 'milestone.status.pending' })
              }
              sx={{
                ml: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: 14,
                  color: colorPalette.monotone[9],
                },
              }}
            />
          </Box>
        )}

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            {intl.formatMessage({ id: 'milestone.field.icon' })}
          </Typography>
          <ToggleButtonGroup
            exclusive={true}
            size="small"
            value={formState.iconName}
            onChange={(_, v) => v && onFieldChange('iconName', v)}
          >
            {PRESET_ICONS.map(icon => (
              <ToggleButton key={icon} value={icon} sx={{ p: 0.75 }}>
                <Icon
                  icon={icon}
                  width={24}
                  height={24}
                  color={
                    formState.iconName === icon
                      ? formState.color
                      : colorPalette.monotone[5]
                  }
                />
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            {intl.formatMessage({ id: 'milestone.field.color' })}
          </Typography>
          <ToggleButtonGroup
            exclusive={true}
            size="small"
            value={formState.color}
            onChange={(_, v) => v && onFieldChange('color', v)}
          >
            {PRESET_COLORS.map(color => (
              <ToggleButton key={color} value={color}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: color,
                  }}
                />
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
    </Box>
  </>
)

const TextField = styled(MuiTextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: colorPalette.monotone[2] },
    '&:hover fieldset': { borderColor: colorPalette.skyBlue[7] },
    '&.Mui-focused fieldset': {
      borderColor: colorPalette.skyBlue[7],
      borderWidth: '1px',
    },
  },
  '& .MuiOutlinedInput-input': {
    fontSize: 14,
    color: colorPalette.monotone[9],
    caretColor: colorPalette.skyBlue[7],
    '&:focus': { backgroundColor: colorPalette.blue[0] },
  },
})

export default MilestoneDialog
